import { useState } from 'react'
import {
  connectAiLab,
  connectAiLabRow,
  removeAiLab,
  saveAiLab,
  testAiBrain,
  type AiAgentShell,
  type AiLab,
  type AiLabUsage,
} from '../../api/settingsAiAgent'
import { failed, flagOf, NoteText, shortNum, type GlobalDraft, type Note } from './parts'

/**
 * Fila "AI Labs & Models" - numai la Global.
 *
 * Drumul de sus in jos e chiar cel cerut: alegi laboratorul din catalog
 * (protocolul si adresa vin de acolo), lipesti cheia, Add - se salveaza SI se
 * conecteaza, iar raspunsul spune pe loc daca cheia merge. Sub carduri, pliat,
 * cheia comuna si modelul de rezerva (folosite doar cand o treapta n-are lab)
 * plus serviciul-creier, cu butonul lui de proba.
 *
 * CHEILE NU SE VAD NICIODATA. Casuta arata `••••` cand exista una si "leave
 * empty to keep it": o cheie trimisa goala lasa neatinsa cea din baza.
 */
export function LabsTab({
  page,
  draft,
  onDraft,
  onSaveGlobal,
  onReload,
}: {
  page: AiAgentShell
  draft: GlobalDraft
  onDraft(next: GlobalDraft): void
  onSaveGlobal(): void
  onReload(): void
}) {
  return (
    <>
      {!page.labsReady ? (
        <div className="aia-note-warn">
          The labs registry is missing - apply the <code>2026_09_04_100007_ai_labs</code> migration. Until then the
          tiers use the shared key below.
        </div>
      ) : null}

      <AddLab page={page} onAdded={onReload} />

      {page.aiLabs.length > 0 ? (
        <div className="aia-lab-grid">
          {page.aiLabs.map((lab) => (
            <LabCard key={lab.id} lab={lab} usage={page.labUsage[String(lab.id)] ?? null} onChanged={onReload} />
          ))}
        </div>
      ) : (
        <p className="aia-help">
          No lab yet. Pick one above, paste its key and press Add lab - it connects on the spot and its models show up
          in every dropdown.
        </p>
      )}

      <Fallback page={page} draft={draft} onDraft={onDraft} onSaveGlobal={onSaveGlobal} />
    </>
  )
}

function AddLab({ page, onAdded }: { page: AiAgentShell; onAdded(): void }) {
  const entries = Object.values(page.labCatalog)
  const [catalog, setCatalog] = useState(entries[0]?.key ?? 'anthropic')
  const [apiKey, setApiKey] = useState('')
  const [baseUrl, setBaseUrl] = useState('')
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState<Note | null>(null)

  const spec = page.labCatalog[catalog]
  const needsUrl = spec?.protocol === 'custom'

  const add = () => {
    setBusy(true)
    setNote({ text: 'Saving and connecting…' })

    void saveAiLab({ catalog, base_url: baseUrl, api_key: apiKey, enabled: 1, connect: 1 })
      .then((r) => {
        setBusy(false)
        if (!r.ok) {
          setNote({ text: r.error ?? 'Failed', bad: true })
          return
        }

        const c = r.connect
        if (c?.ok) {
          setNote({
            text:
              'Connected - ' +
              (c.no_list ? 'no model list, type the model id at the tier' : `${c.models.length} models`),
          })
        } else {
          /* Ramane in lista, cu motivul: cheia se corecteaza pe rand si se
             apasa Connect. */
          setNote({
            text: `Added, but Connect failed: ${c?.error ?? 'unknown reason'} - fix the key on its row and press Connect.`,
            bad: true,
          })
        }

        setApiKey('')
        onAdded()
      })
      .catch((e: unknown) => {
        setBusy(false)
        setNote({ text: failed(e), bad: true })
      })
  }

  return (
    <div className="panel panel-default">
      <div className="panel-body">
        <div className="aia-lab-add">
          <div>
            <label className="aia-help">Lab</label>
            <select className="form-control input-sm" value={catalog} onChange={(e) => setCatalog(e.target.value)}>
              {entries.map((entry) => {
                const flag = flagOf(entry.country)
                return (
                  <option key={entry.key} value={entry.key}>
                    {entry.name}
                    {flag !== '' ? ' ' + flag : ''}
                  </option>
                )
              })}
            </select>
            <span className="aia-help">
              {(spec?.note ?? '') + (spec?.base_url ? ' ' + spec.base_url : '')}
            </span>
          </div>
          <div>
            <label className="aia-help">API key</label>
            <input
              type="password"
              className="form-control input-sm"
              autoComplete="new-password"
              placeholder="paste the lab key"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
            />
          </div>
          {needsUrl ? (
            <div>
              <label className="aia-help">Base URL</label>
              <input
                type="text"
                className="form-control input-sm"
                placeholder="http://10.0.0.5:11434"
                value={baseUrl}
                onChange={(e) => setBaseUrl(e.target.value)}
              />
            </div>
          ) : null}
          <div>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={busy || !page.labsReady}
              onClick={add}
            >
              Add lab
            </button>{' '}
            <NoteText note={note} />
          </div>
        </div>
      </div>
    </div>
  )
}

function LabCard({ lab, usage, onChanged }: { lab: AiLab; usage: AiLabUsage | null; onChanged(): void }) {
  const [name, setName] = useState(lab.name)
  const [apiKey, setApiKey] = useState('')
  const [baseUrl, setBaseUrl] = useState(lab.base_url)
  const [enabled, setEnabled] = useState(lab.enabled)
  const [note, setNote] = useState<Note | null>(null)

  const save = () => {
    setNote({ text: '…' })
    void saveAiLab({
      id: lab.id,
      name,
      catalog: lab.catalog,
      base_url: baseUrl,
      api_key: apiKey,
      enabled: enabled ? 1 : 0,
    })
      .then((r) => {
        setNote(r.ok ? { text: 'Saved' } : { text: r.error ?? 'Failed', bad: true })
        if (r.ok) setApiKey('')
      })
      .catch((e: unknown) => setNote({ text: failed(e), bad: true }))
  }

  const connect = () => {
    setNote({ text: 'connecting…' })
    void connectAiLabRow(lab.id)
      .then((r) => {
        if (!r.ok) {
          setNote({ text: r.error ?? 'Connect failed', bad: true })
          return
        }
        setNote({ text: 'Connected' })
        onChanged()
      })
      .catch((e: unknown) => setNote({ text: failed(e), bad: true }))
  }

  const remove = () => {
    if (!window.confirm('Remove this lab? Tiers and companies pointing at it fall back to the shared key.')) return

    void removeAiLab(lab.id)
      .then((r) => (r.ok ? onChanged() : setNote({ text: 'Could not remove', bad: true })))
      .catch((e: unknown) => setNote({ text: failed(e), bad: true }))
  }

  return (
    <div className={lab.enabled ? 'aia-lab-card' : 'aia-lab-card is-off'}>
      <div className="aia-lab-head">
        <div className="aia-lab-title">
          <input
            type="text"
            className="form-control input-sm"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          {/* Starea ultimului Connect: verde cu numarul de modele, rosu cu motivul, gri pana la primul. */}
          {lab.status === 'connected' ? (
            <span className="aia-chip aia-chip-ok" title={'connected ' + lab.connected_at.substring(0, 16)}>
              Connected{lab.models.length > 0 ? ` · ${lab.models.length} models` : ' · type the model id'}
            </span>
          ) : lab.status === 'failed' ? (
            <span className="aia-chip aia-chip-bad" title={lab.last_error}>
              Failed · {lab.last_error.substring(0, 60)}
            </span>
          ) : (
            <span className="aia-chip aia-chip-muted">Not connected</span>
          )}
        </div>
        <span className="aia-help">
          {lab.catalog_name}
          {lab.base_url !== '' ? ' · ' + lab.base_url : ''}
        </span>
      </div>

      <div className="aia-lab-body">
        <input
          type="password"
          className="form-control input-sm"
          autoComplete="new-password"
          placeholder={lab.has_key ? '•••••••••• (set - leave empty to keep it)' : 'no key yet - paste one'}
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
        />
        {lab.provider === 'custom' ? (
          <input
            type="text"
            className="form-control input-sm"
            placeholder="http://10.0.0.5:11434"
            value={baseUrl}
            onChange={(e) => setBaseUrl(e.target.value)}
          />
        ) : null}
        <div className="aia-lab-models">
          {lab.models.length > 0 ? (
            <div className="aia-help aia-lab-model-list">
              {lab.models.slice(0, 8).map((m) => m.id).join(', ')}
              {lab.models.length > 8 ? ', ...' : ''}
            </div>
          ) : lab.status === 'connected' ? (
            <span className="aia-help">this lab does not list its models - type the model id at the tier</span>
          ) : (
            <span className="aia-help">no models yet - fix the key and press Connect</span>
          )}
        </div>
      </div>

      {usage ? <LabUsageBlock usage={usage} /> : null}

      <div className="aia-actions" style={{ marginTop: 8 }}>
        <button type="button" className="btn btn-default btn-sm" onClick={save}>
          Save
        </button>
        <button type="button" className="btn btn-default btn-sm" onClick={connect}>
          Connect
        </button>
        <label className="aia-switch">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />{' '}
          <span>enabled</span>
        </label>
        <button type="button" className="btn btn-link btn-sm" style={{ color: '#b42318' }} onClick={remove}>
          Remove
        </button>
        <NoteText note={note} />
      </div>
    </div>
  )
}

/**
 * Cat s-a folosit si cat a costat PE LABORATORUL ASTA: cheia se plateste pe
 * laborator, deci si cifra trebuie sa stea langa ea.
 */
function LabUsageBlock({ usage }: { usage: AiLabUsage }) {
  return (
    <details className="aia-lab-usage">
      <summary>
        {usage.messages} answers · {shortNum(usage.tokens_in)} in · {shortNum(usage.tokens_out)} out
        {usage.cost > 0 ? (
          <>
            {' '}
            · {usage.currency} {usage.cost.toFixed(2)}
            {usage.priced ? '' : '+'}
          </>
        ) : null}
        <span className="aia-help"> · last 30 days</span>
      </summary>
      <table className="aia-lab-usage-table">
        <tbody>
          <tr>
            <th>Model</th>
            <th>Answers</th>
            <th>In</th>
            <th>Out</th>
            <th>Cost</th>
            <th>Last</th>
          </tr>
          {usage.models.map((row) => (
            <tr key={row.model}>
              <td>{row.model}</td>
              <td>{row.messages}</td>
              <td>{shortNum(row.tokens_in)}</td>
              <td>{shortNum(row.tokens_out)}</td>
              <td>
                {row.cost === null ? (
                  <span className="aia-help" title="no price written for this model">
                    no price
                  </span>
                ) : (
                  `${row.currency} ${row.cost.toFixed(2)}`
                )}
              </td>
              <td className="aia-help">{String(row.last).substring(0, 10)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {!usage.priced ? (
        <div className="aia-help">
          The total carries a <strong>+</strong>: at least one model has no price written, so its cost is missing from
          it.
        </div>
      ) : null}
    </details>
  )
}

/** Cheia comuna, modelul de rezerva si serviciul-creier - cu butonul de proba. */
function Fallback({
  page,
  draft,
  onDraft,
  onSaveGlobal,
}: {
  page: AiAgentShell
  draft: GlobalDraft
  onDraft(next: GlobalDraft): void
  onSaveGlobal(): void
}) {
  const [models, setModels] = useState<{ id: string; name: string }[]>(
    page.ai.model !== '' ? [{ id: page.ai.model, name: page.ai.model + ' (current)' }] : []
  )
  const [connectNote, setConnectNote] = useState<Note | null>(null)
  const [brainNote, setBrainNote] = useState<Note | null>(null)

  const connect = () => {
    setConnectNote({ text: 'Connecting…' })
    void connectAiLab({ scope: 'global', lab: draft.fallbackLab, base_url: draft.fallbackBaseUrl, api_key: draft.apiKey })
      .then((r) => {
        if (!r.ok) {
          setConnectNote({ text: r.error ?? 'Connect failed.', bad: true })
          return
        }
        setModels(r.models)
        setConnectNote({ text: `Connected - ${r.models.length} models.` })
      })
      .catch((e: unknown) => setConnectNote({ text: failed(e), bad: true }))
  }

  const test = () => {
    setBrainNote({ text: 'Testing…' })
    void testAiBrain(draft.brainUrl)
      .then((r) => setBrainNote({ text: r.text, bad: !r.ok }))
      .catch((e: unknown) => setBrainNote({ text: failed(e), bad: true }))
  }

  return (
    <details className="aia-advanced" id="odai-advanced">
      <summary>Fallback (shared key and model) and brain service</summary>
      <div className="aia-advanced-body">
        <div className="aia-advanced-col">
          <p className="aia-help" style={{ margin: '0 0 8px' }}>
            <strong>Shared key and fallback model</strong> - the old global setting; used only when a tier has no lab
            or no model.
          </p>
          <div className="form-group">
            <label>AI Lab</label>
            <select
              className="form-control"
              value={draft.fallbackLab}
              onChange={(e) => onDraft({ ...draft, fallbackLab: e.target.value })}
            >
              {Object.entries(page.labs).map(([key, spec]) => (
                <option key={key} value={key}>
                  {spec.name}
                </option>
              ))}
            </select>
          </div>
          {draft.fallbackLab === 'custom' ? (
            <div className="form-group">
              <label>Model address (local / self-hosted)</label>
              <input
                type="text"
                className="form-control"
                placeholder="http://10.0.0.5:11434 (Ollama, vLLM, LM Studio...)"
                value={draft.fallbackBaseUrl}
                onChange={(e) => onDraft({ ...draft, fallbackBaseUrl: e.target.value })}
              />
              <span className="help-block" style={{ fontSize: 12 }}>
                OpenAI-compatible endpoint. The key can be left empty for a local model with no authentication.
              </span>
            </div>
          ) : null}
          <div className="form-group">
            <label>API key</label>
            <input
              type="password"
              className="form-control"
              autoComplete="new-password"
              placeholder={page.ai.has_key ? '•••••••••• (set - leave empty to keep it)' : 'the lab key'}
              value={draft.apiKey}
              onChange={(e) => onDraft({ ...draft, apiKey: e.target.value })}
            />
          </div>
          <div className="form-group">
            <button type="button" className="btn btn-default" onClick={connect}>
              Connect
            </button>{' '}
            <NoteText note={connectNote} />
          </div>
          <div className="form-group">
            <label>Model default</label>
            <select
              className="form-control"
              value={draft.fallbackModel}
              onChange={(e) => onDraft({ ...draft, fallbackModel: e.target.value })}
            >
              <option value="">-- model --</option>
              {models.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
              {draft.fallbackModel !== '' && !models.some((m) => m.id === draft.fallbackModel) ? (
                <option value={draft.fallbackModel}>{draft.fallbackModel}</option>
              ) : null}
            </select>
            <span className="help-block" style={{ fontSize: 12 }}>
              Press Connect to reload the list from the lab.
            </span>
          </div>
        </div>

        <div className="aia-advanced-col">
          <p className="aia-help" style={{ margin: '0 0 8px' }}>
            <strong>Brain service (od-mcp)</strong> - optional. Empty = the portal calls the model itself.
          </p>
          <div className="form-group">
            <label>od-mcp address</label>
            <input
              type="text"
              className="form-control"
              placeholder="http://127.0.0.1:8765"
              value={draft.brainUrl}
              onChange={(e) => onDraft({ ...draft, brainUrl: e.target.value })}
            />
            <div style={{ marginTop: 6 }}>
              <button type="button" className="btn btn-default btn-sm" onClick={test}>
                Test connection
              </button>{' '}
              <NoteText note={brainNote} />
            </div>
          </div>
          <div className="form-group">
            <label>Brain token</label>
            <input
              type="password"
              className="form-control"
              autoComplete="new-password"
              placeholder={
                page.ai.has_brain_token
                  ? '•••••••••• (set - leave empty to keep it)'
                  : 'the same OD_BRAIN_TOKEN as the service'
              }
              value={draft.brainToken}
              onChange={(e) => onDraft({ ...draft, brainToken: e.target.value })}
            />
          </div>
        </div>
      </div>
      <div className="aia-actions">
        <button type="button" className="btn btn-primary btn-sm" onClick={onSaveGlobal}>
          Save
        </button>
      </div>
    </details>
  )
}
